import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { getUserId } from "@/lib/user";
import { STORAGE_EVENTS } from "@/constants/storage";

export type ConversationSummary = {
  id: string;
  title: string;
  mode: string;
  created_at: string;
  updated_at: string;
};

// useConversationList — recent conversations for the signed-in user.
// `mode` scopes the tray to a single surface (e.g. "consumer_rent");
// omit it to list every mode the user has touched.
export function useConversationList(mode?: string) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId, limit: "50" });
      if (mode) params.set("mode", mode);
      const res = await apiFetch(`/conversations?${params.toString()}`);
      if (!res.ok) return;
      const body = (await res.json()) as { conversations?: ConversationSummary[] };
      setConversations(body.conversations ?? []);
    } catch {
      // ignore — sidebar just stays empty
    } finally {
      setLoading(false);
    }
  }, [mode]);

  const deleteConversation = useCallback(async (id: string) => {
    await apiFetch(`/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const renameConversation = useCallback(async (id: string, title: string) => {
    await apiFetch(`/conversations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title }),
    });
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title } : c))
    );
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refresh whenever a new conversation is created in any chat instance.
  useEffect(() => {
    function onCreated() { void refresh(); }
    window.addEventListener(STORAGE_EVENTS.conversationCreated, onCreated);
    return () => window.removeEventListener(STORAGE_EVENTS.conversationCreated, onCreated);
  }, [refresh]);

  return { conversations, loading, refresh, deleteConversation, renameConversation };
}
